import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow, updateRows } from "@/lib/adminApi";
import {
  BLOG_CATEGORIES,
  BLOG_STATUS,
  optionsFrom,
  type BlogPost,
  type GalleryItem,
} from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { CoverPicker, GalleryPicker } from "@/pages/admin/components/MediaPicker";
import { GalleryCell } from "@/pages/admin/components/GalleryCell";
import { contentStatusMeta, formatSchedule, toDateTimeLocal } from "@/lib/publishSchedule";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_POST = {
  title: "",
  slug: "",
  category: "Guides",
  excerpt: "",
  content: "",
  author: "Rythugaru Team",
  cover_image: "",
  tags: "",
  gallery: [] as GalleryItem[],
  featured: false,
  status: "draft",
  scheduled_for: "",
};

export default function AdminBlog() {
  const [rows, setRows] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_POST });
  const [coverPreview, setCoverPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<BlogPost>("blog_posts", "title"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load blog posts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const galleryMap = useAssetUrlMap(
    rows.flatMap((row) => (row.gallery || []).map((item) => item.src)),
  );

  const update = async (id: number, patch: Partial<BlogPost>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("blog_posts", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.filter((row) => row.id !== id));
    setSelectedId(null);
    try {
      await deleteRow("blog_posts", id);
      notify(`${current?.title || "Post"} deleted.`);
    } catch {
      setError("We couldn't delete that post. Please try again.");
      load();
    }
  };

  const createPost = async () => {
    if (!draft.title.trim()) {
      setError("A post title is required.");
      return;
    }
    if (draft.status === "scheduled" && !draft.scheduled_for) {
      setError("Pick a date and time to schedule this post.");
      return;
    }
    setSaving(true);
    try {
      const tags = draft.tags.split(",").map((tag) => tag.trim()).filter(Boolean);
      const slug =
        draft.slug.trim() ||
        draft.title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const created = await insertRow<BlogPost>("blog_posts", {
        title: draft.title.trim(),
        slug,
        category: draft.category,
        excerpt: draft.excerpt,
        content: draft.content,
        author: draft.author || "Rythugaru Team",
        cover_image: draft.cover_image || null,
        tags,
        gallery: draft.gallery,
        featured: draft.featured,
        status: draft.status,
        views: 0,
        scheduled_for:
          draft.status === "scheduled" && draft.scheduled_for
            ? new Date(draft.scheduled_for).toISOString()
            : null,
        published_at: draft.status === "published" ? new Date().toISOString() : null,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_POST });
      setCoverPreview("");
      notify(`${created.title} added to the blog.`);
    } catch {
      setError("We couldn't create that post. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (categoryFilter !== "all" && row.category !== categoryFilter) return false;
      if (!query) return true;
      return [row.title, row.author || "", row.excerpt || "", (row.tags || []).join(" ")].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, categoryFilter]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = filtered.length > 0 && filtered.every((row) => selectedSet.has(row.id));

  const toggleSelect = (id: number) => {
    setSelectedIds((list) => (list.includes(id) ? list.filter((value) => value !== id) : [...list, id]));
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((list) => list.filter((id) => !filtered.some((row) => row.id === id)));
    } else {
      setSelectedIds((list) => Array.from(new Set([...list, ...filtered.map((row) => row.id)])));
    }
  };

  const bulkSetStatus = async (status: string, message: string) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setRows((list) => list.map((row) => (ids.includes(row.id) ? { ...row, status } : row)));
    try {
      await updateRows("blog_posts", ids, {
        status,
        published_at: status === "published" ? new Date().toISOString() : null,
      });
      notify(message);
      setSelectedIds([]);
    } catch {
      setError("We couldn't update those posts. Please try again.");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const selected = rows.find((row) => row.id === selectedId) || null;
  const totalViews = rows.reduce((sum, row) => sum + Number(row.views || 0), 0);

  const columns: Column<BlogPost>[] = [
    {
      key: "title",
      header: "Post",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.title}</p>
          <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.excerpt || "—"}</p>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">{row.category || "—"}</span>
      ),
    },
    { key: "author", header: "Author", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.author || "—"}</span> },
    { key: "views", header: "Views", render: (row) => <span className="text-sm text-foreground-700">{row.views.toLocaleString("en-IN")}</span> },
    {
      key: "featured",
      header: "Featured",
      render: (row) =>
        row.featured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2 py-1 rounded-full whitespace-nowrap">
            <i className="ri-star-fill"></i> Yes
          </span>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...contentStatusMeta(BLOG_STATUS, row.status, row.scheduled_for)} /> },
    { key: "gallery", header: "Gallery", render: (row) => <GalleryCell items={row.gallery} pathMap={galleryMap} /> },
    {
      key: "published",
      header: "Publish date",
      render: (row) => (
        <span className="text-sm text-foreground-600 whitespace-nowrap">
          {row.status === "scheduled"
            ? formatSchedule(row.scheduled_for)
            : row.published_at
              ? formatDate(row.published_at)
              : "—"}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Blog"
        subtitle="Field notes, seasonal guides and stories that bring visitors closer to the land."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Post
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total posts", value: rows.length, icon: "ri-article-line", tone: "primary" },
          { label: "Published", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Drafts & review", value: rows.filter((row) => row.status === "draft" || row.status === "review").length, icon: "ri-draft-line", tone: "accent" },
          { label: "Total views", value: totalViews.toLocaleString("en-IN"), icon: "ri-eye-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search posts by title, author, tag…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(BLOG_STATUS)]} />
          <Select label="Category" value={categoryFilter} onChange={setCategoryFilter} options={[{ value: "all", label: "All categories" }, ...BLOG_CATEGORIES.map((category) => ({ value: category, label: category }))]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} posts</span>
        </FilterBar>

        {selectedIds.length > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-foreground-800 flex-1">
              {selectedIds.length} post{selectedIds.length === 1 ? "" : "s"} selected
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => bulkSetStatus("published", `${selectedIds.length} post${selectedIds.length === 1 ? "" : "s"} published.`)}
                disabled={bulkBusy}
                className={btnPrimary}
              >
                {bulkBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-checkbox-circle-line"></i>} Publish
              </button>
              <button
                type="button"
                onClick={() => bulkSetStatus("draft", `${selectedIds.length} post${selectedIds.length === 1 ? "" : "s"} moved to draft.`)}
                disabled={bulkBusy}
                className={btnGhost}
              >
                <i className="ri-draft-line"></i> Unpublish
              </button>
              <button type="button" onClick={() => setSelectedIds([])} className={btnGhost}>
                <i className="ri-close-line"></i> Clear
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <LoadingState label="Loading posts…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-article-line" title={rows.length === 0 ? "No posts yet" : "No posts match"} message="Blog posts you publish will appear here." />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            onRowClick={(row) => setSelectedId(row.id)}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleAll}
            allSelected={allSelected}
          />
        )}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelectedId(null)} title={selected?.title || "Post"} subtitle="Blog post" footer={selected ? (
        <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
          <i className="ri-delete-bin-line"></i> Delete post
        </button>
      ) : null}>
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...contentStatusMeta(BLOG_STATUS, selected.status, selected.scheduled_for)} />
              {selected.category ? (
                <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">{selected.category}</span>
              ) : null}
            </div>

            <DetailList
              items={[
                { icon: "ri-user-line", label: "Author", value: selected.author || "—" },
                { icon: "ri-eye-line", label: "Views", value: selected.views.toLocaleString("en-IN") },
                {
                  icon: "ri-calendar-line",
                  label: selected.status === "scheduled" ? "Scheduled for" : "Published",
                  value:
                    selected.status === "scheduled"
                      ? formatSchedule(selected.scheduled_for)
                      : selected.published_at
                        ? formatDate(selected.published_at)
                        : "Not published",
                },
              ]}
            />

            {selected.excerpt ? (
              <Field label="Excerpt">
                <p className="text-sm text-foreground-700 leading-relaxed">{selected.excerpt}</p>
              </Field>
            ) : null}

            {selected.tags && selected.tags.length > 0 ? (
              <Field label="Tags">
                <div className="flex flex-wrap gap-2">
                  {selected.tags.map((tag) => (
                    <span key={tag} className="text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </Field>
            ) : null}

            {selected.content ? (
              <Field label="Content preview">
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-6">{selected.content}</p>
              </Field>
            ) : null}

            <Field label="Photo gallery">
              <GalleryPicker
                label="Gallery photos"
                value={selected.gallery || []}
                onChange={(items) => update(selected.id, { gallery: items }, "Gallery updated.")}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Publication status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Post status updated.")}
                  className={fieldInput}
                >
                  {optionsFrom(BLOG_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Featured">
                <button
                  type="button"
                  onClick={() => update(selected.id, { featured: !selected.featured }, selected.featured ? "Removed from featured." : "Marked as featured.")}
                  className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                    selected.featured ? "bg-accent-500 text-accent-950 border-accent-500" : "bg-background-100 text-foreground-700 border-background-300"
                  }`}
                >
                  <i className={selected.featured ? "ri-star-fill" : "ri-star-line"}></i>
                  {selected.featured ? "Featured" : "Not featured"}
                </button>
              </Field>
            </div>

            {selected.status === "scheduled" ? (
              <Field label="Publish automatically on">
                <input
                  type="datetime-local"
                  value={toDateTimeLocal(selected.scheduled_for)}
                  onChange={(event) =>
                    update(
                      selected.id,
                      {
                        scheduled_for: event.target.value
                          ? new Date(event.target.value).toISOString()
                          : null,
                      },
                      "Schedule updated.",
                    )
                  }
                  className={fieldInput}
                />
                <p className="text-xs text-foreground-500 mt-2">
                  The post goes live on the public journal automatically at this time.
                </p>
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Write a new post"
        subtitle="New blog post"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createPost} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create post
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Title">
            <input
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              placeholder="e.g. Monsoon in Konaseema"
              className={textInput}
            />
          </TextField>
          <TextField label="Category">
            <select
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              className={fieldInput}
            >
              {BLOG_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </TextField>
          <TextField label="Author">
            <input
              value={draft.author}
              onChange={(event) => setDraft({ ...draft, author: event.target.value })}
              className={textInput}
            />
          </TextField>
          <TextField label="Slug">
            <input
              value={draft.slug}
              onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
              placeholder="auto-generated from title"
              className={textInput}
            />
          </TextField>
          <div className="sm:col-span-2">
            <CoverPicker
              label="Cover image"
              value={draft.cover_image}
              preview={coverPreview}
              onChange={(value, preview) => {
                setDraft({ ...draft, cover_image: value });
                setCoverPreview(preview);
              }}
            />
          </div>
          <TextField label="Tags (comma separated)">
            <input
              value={draft.tags}
              onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
              placeholder="e.g. monsoon, paddy, season"
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(BLOG_STATUS).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </TextField>
          <TextField label="Featured">
            <button
              type="button"
              onClick={() => setDraft({ ...draft, featured: !draft.featured })}
              className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                draft.featured
                  ? "bg-accent-500 text-accent-950 border-accent-500"
                  : "bg-background-100 text-foreground-700 border-background-300"
              }`}
            >
              <i className={draft.featured ? "ri-star-fill" : "ri-star-line"}></i>
              {draft.featured ? "Featured" : "Not featured"}
            </button>
          </TextField>
          {draft.status === "scheduled" ? (
            <div className="sm:col-span-2">
              <TextField label="Publish automatically on">
                <input
                  type="datetime-local"
                  value={draft.scheduled_for}
                  onChange={(event) => setDraft({ ...draft, scheduled_for: event.target.value })}
                  className={fieldInput}
                />
              </TextField>
            </div>
          ) : null}
          <div className="sm:col-span-2">
            <GalleryPicker
              label="Photo gallery"
              value={draft.gallery}
              onChange={(items) => setDraft({ ...draft, gallery: items })}
            />
          </div>
          <div className="sm:col-span-2">
            <TextField label="Excerpt">
              <textarea
                value={draft.excerpt}
                onChange={(event) => setDraft({ ...draft, excerpt: event.target.value })}
                rows={2}
                maxLength={500}
                placeholder="A short summary shown on the blog index…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
          <div className="sm:col-span-2">
            <TextField label="Content">
              <textarea
                value={draft.content}
                onChange={(event) => setDraft({ ...draft, content: event.target.value })}
                rows={5}
                placeholder="Write the full post…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
        </div>
      </Modal>
      {node}
    </div>
  );
}